import axios from 'axios';

/**
 * Mock service to simulate timeout errors for API endpoints
 *
 * Usage:
 * 1. Enable mocking: localStorage.setItem('mockTimeoutApi', 'true')
 * 2. Disable mocking: localStorage.removeItem('mockTimeoutApi')
 *
 * Or set VUE_APP_MOCK_TIMEOUT_API=true in .env file
 *
 * To mock specific endpoints, use:
 * localStorage.setItem('mockTimeoutApiEndpoints', JSON.stringify(['/homepage/middle-nav-icons', '/homepage/featured']))
 *
 * To set custom timeout duration (default: 20000ms), use:
 * localStorage.setItem('mockTimeoutApiDuration', '30000')
 */

let isMockingEnabled = false;
let requestInterceptorId = null;
let mockEndpoints = [];
let timeoutDuration = 20000; // Default 20 seconds
let failImmediately = false; // Flag to make pending requests fail immediately (0ms timeout)
let failOnceMode = false; // Flag to fail only once, then succeed on subsequent requests
let hasFailedOnce = false; // Track if we've already failed once in fail-once mode

/**
 * Check if mocking should be enabled
 */
function shouldMock() {
  if (typeof window === 'undefined') return false;

  // If fail-once mode is enabled and we've already failed once, don't mock
  if (failOnceMode && hasFailedOnce) {
    return false;
  }

  // Check localStorage
  const localStorageMock = localStorage.getItem('mockTimeoutApi');
  if (localStorageMock === 'true') return true;

  // Check environment variable
  return process.env.VUE_APP_MOCK_TIMEOUT_API === 'true';
}

/**
 * Check if fail-once mode is enabled
 */
function isFailOnceMode() {
  if (typeof window === 'undefined') return false;

  // Check localStorage first
  const localStorageFailOnce = localStorage.getItem('mockTimeoutApiFailOnce');
  if (localStorageFailOnce === 'true') return true;

  // Check environment variable
  return process.env.VUE_APP_MOCK_TIMEOUT_API_FAIL_ONCE === 'true';
}

/**
 * Get list of endpoints to mock (from localStorage or default to all)
 */
function getMockEndpoints() {
  if (typeof window === 'undefined') return [];

  try {
    const storedEndpoints = localStorage.getItem('mockTimeoutApiEndpoints');
    if (storedEndpoints) {
      return JSON.parse(storedEndpoints);
    }
  } catch (e) {
    console.warn('[MOCK TIMEOUT] Failed to parse mockTimeoutApiEndpoints:', e);
  }

  // Default: mock all API endpoints
  return null; // null means mock all
}

/**
 * Get timeout duration from localStorage or use default
 */
function getTimeoutDuration() {
  if (typeof window === 'undefined') return timeoutDuration;

  try {
    const storedDuration = localStorage.getItem('mockTimeoutApiDuration');
    if (storedDuration) {
      const duration = parseInt(storedDuration, 10);
      if (!isNaN(duration) && duration > 0) {
        return duration;
      }
    }
  } catch (e) {
    console.warn('[MOCK TIMEOUT] Failed to parse mockTimeoutApiDuration:', e);
  }

  return timeoutDuration;
}

/**
 * Check if a URL should be mocked
 */
function shouldMockUrl(url) {
  if (!url) return false;

  const apiUrl = process.env.VUE_APP_API_URL || '';
  if (!url.includes(apiUrl)) return false;

  // If no specific endpoints configured, mock all requests to the main API
  if (!mockEndpoints || mockEndpoints.length === 0) {
    return true;
  }

  // Check if URL matches any of the configured endpoints
  return mockEndpoints.some(endpoint => url.includes(endpoint));
}

/**
 * Create a mock timeout error
 */
function createTimeoutError(config) {
  const error = new Error(`Timeout of ${getTimeoutDuration()}ms exceeded`);
  error.code = 'ECONNABORTED';
  error.config = config;
  error.isAxiosError = true;
  error.message = `timeout of ${getTimeoutDuration()}ms exceeded`;

  // Simulate axios timeout error structure
  error.response = undefined;
  error.request = {
    ...config,
    timeout: getTimeoutDuration(),
  };

  return error;
}

/**
 * Mock axios request interceptor - intercepts requests and returns timeout errors
 */
function mockAxiosRequestInterceptor(config) {
  const url = config.url || '';

  if (!shouldMock() || !shouldMockUrl(url)) {
    return config;
  }

  const method = config.method?.toLowerCase() || 'get';
  // Use 0ms timeout if immediate failure is enabled (for testing reload functionality)
  const duration = failImmediately ? 0 : getTimeoutDuration();
  console.warn(
    `[MOCK TIMEOUT] ⚠️ Simulating timeout error for ${method.toUpperCase()} ${url} (will timeout after ${duration}ms)`,
  );

  // Create a custom adapter that returns timeout error after specified duration
  config.adapter = () => {
    return new Promise((_, reject) => {
      setTimeout(() => {
        // If fail-once mode is enabled, mark that we've failed once
        if (failOnceMode) {
          hasFailedOnce = true;
          // Set a flag in localStorage so we can detect reload
          if (typeof window !== 'undefined') {
            localStorage.setItem('mockTimeoutApiHasFailed', 'true');
          }
          // Failed once in fail-once mode - subsequent requests will succeed
        }
        reject(createTimeoutError(config));
      }, duration);
    });
  };

  return config;
}

/**
 * Initialize mock service
 */
export function initMockTimeoutApi() {
  // Remove existing interceptors if any
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }

  // Reset immediate failure flag
  failImmediately = false;

  // Check if fail-once mode is enabled
  failOnceMode = isFailOnceMode();

  // Auto-detect reload scenario: if fail-once mode is enabled and we've failed before,
  // and initMockTimeoutApi is being called again, it means we're reloading
  // Automatically reset the fail-once flag so APIs will succeed on retry
  if (failOnceMode && typeof window !== 'undefined') {
    const hasFailed = localStorage.getItem('mockTimeoutApiHasFailed') === 'true';
    const resetFailOnce = localStorage.getItem('mockTimeoutApiResetFailOnce');

    if (resetFailOnce === 'true') {
      // Explicit reset requested
      hasFailedOnce = false;
      localStorage.removeItem('mockTimeoutApiResetFailOnce');
      localStorage.removeItem('mockTimeoutApiHasFailed');
      // Reset fail-once flag - will fail on next request
    } else if (hasFailed && requestInterceptorId === null) {
      // Auto-detect reload: if we've failed before and interceptor was removed (re-initialization),
      // it means we're reloading - reset the flag automatically
      hasFailedOnce = false;
      localStorage.removeItem('mockTimeoutApiHasFailed');
      // Auto-detected reload - reset fail-once flag, APIs will succeed on retry
    } else if (hasFailed) {
      // We've failed before, keep track of it
      hasFailedOnce = true;
    }
  }

  if (shouldMock()) {
    mockEndpoints = getMockEndpoints();
    timeoutDuration = getTimeoutDuration();
    console.warn('[MOCK TIMEOUT] ⚠️ Mock Timeout API enabled', {
      endpoints: mockEndpoints || 'ALL',
      timeoutDuration: `${timeoutDuration}ms`,
      failOnceMode: failOnceMode,
    });

    // Use request interceptor to catch requests before they're sent
    requestInterceptorId = axios.interceptors.request.use(
      config => mockAxiosRequestInterceptor(config),
      error => Promise.reject(error),
    );

    isMockingEnabled = true;
  } else {
    // Mock Timeout API disabled - no log needed unless debugging
    isMockingEnabled = false;
  }
}

/**
 * Enable mocking programmatically
 */
export function enableMocking(endpoints = null, duration = null) {
  localStorage.setItem('mockTimeoutApi', 'true');
  if (endpoints) {
    localStorage.setItem('mockTimeoutApiEndpoints', JSON.stringify(endpoints));
  }
  if (duration) {
    localStorage.setItem('mockTimeoutApiDuration', String(duration));
  }
  initMockTimeoutApi();
}

/**
 * Disable mocking programmatically
 */
export function disableMocking() {
  localStorage.removeItem('mockTimeoutApi');
  localStorage.removeItem('mockTimeoutApiEndpoints');
  localStorage.removeItem('mockTimeoutApiDuration');
  initMockTimeoutApi();
}

/**
 * Check if mocking is currently enabled
 */
export function isMockingActive() {
  return isMockingEnabled;
}

/**
 * Enable immediate failure for pending requests (0ms timeout)
 * Used when reload is clicked to make pending requests fail immediately
 * so retry can proceed quickly
 */
export function enableImmediateFailure() {
  failImmediately = true;
  console.warn(
    '[MOCK TIMEOUT] ⚠️ Immediate failure enabled - pending requests will fail immediately',
  );
}

/**
 * Enable fail-once mode: fail on first request, then succeed on subsequent requests
 * Usage: localStorage.setItem('mockTimeoutApiFailOnce', 'true')
 * To reset: localStorage.setItem('mockTimeoutApiResetFailOnce', 'true') then call initMockTimeoutApi()
 */
export function enableFailOnceMode() {
  if (typeof window !== 'undefined') {
    localStorage.setItem('mockTimeoutApiFailOnce', 'true');
    hasFailedOnce = false; // Reset the flag
    failOnceMode = true;
    initMockTimeoutApi(); // Re-initialize to pick up the new mode
    console.warn('[MOCK TIMEOUT] ⚠️ Fail-once mode enabled');
  }
}

/**
 * Disable fail-once mode
 */
export function disableFailOnceMode() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('mockTimeoutApiFailOnce');
    localStorage.removeItem('mockTimeoutApiResetFailOnce');
    hasFailedOnce = false;
    failOnceMode = false;
    initMockTimeoutApi(); // Re-initialize
    // Fail-once mode disabled
  }
}

/**
 * Reset fail-once flag (allows testing fail-once behavior again)
 * This is called when reload is clicked to allow APIs to succeed on retry
 */
export function resetFailOnce() {
  if (typeof window !== 'undefined') {
    localStorage.setItem('mockTimeoutApiResetFailOnce', 'true');
    hasFailedOnce = false;
    initMockTimeoutApi(); // Re-initialize to reset the flag
    // Reset fail-once flag - will fail on next request
  }
}

/**
 * Mark that reload is happening - this will be detected by initMockTimeoutApi
 * This can be called externally if needed, but initMockTimeoutApi will also auto-detect reloads
 */
export function markReload() {
  if (typeof window !== 'undefined' && failOnceMode) {
    localStorage.setItem('mockTimeoutApiResetFailOnce', 'true');
    // Reload marked - will reset fail-once flag on next init
  }
}

/**
 * Clear all mock timeout state (localStorage flags and in-memory variables)
 * Use this to completely disable mocking after disabling env variables
 */
export function clearAllMockTimeoutState() {
  if (typeof window !== 'undefined') {
    // Clear all localStorage flags
    localStorage.removeItem('mockTimeoutApi');
    localStorage.removeItem('mockTimeoutApiFailOnce');
    localStorage.removeItem('mockTimeoutApiEndpoints');
    localStorage.removeItem('mockTimeoutApiDuration');
    localStorage.removeItem('mockTimeoutApiHasFailed');
    localStorage.removeItem('mockTimeoutApiResetFailOnce');

    // Reset in-memory variables
    hasFailedOnce = false;
    failOnceMode = false;
    failImmediately = false;

    // Re-initialize to apply changes
    initMockTimeoutApi();

    console.log('[MOCK TIMEOUT] Cleared all mock timeout state');
  }
}

// Initialize on import
initMockTimeoutApi();
